import type { CustomFlowbiteTheme } from 'flowbite-react';

export const buttonTheme: CustomFlowbiteTheme['button'] = {
  base: 'group relative flex cursor-pointer items-stretch justify-center rounded-none p-0.5 text-center font-medium transition-[color,background-color,border-color,text-decoration-color,fill,stroke,box-shadow] focus:z-10 focus:outline-none disabled:cursor-not-allowed',
  fullSized: 'w-full',
  color: {
    dark: 'border border-transparent bg-titan-night-800 text-white focus:ring-4 focus:ring-gray-300 enabled:hover:bg-titan-night-900',
    failure:
      'border border-transparent bg-titan-folly-700 text-white focus:ring-4 focus:ring-titan-folly-300 enabled:hover:bg-titan-folly-800',
    gray: 'border border-transparent bg-gray-700 text-white focus:ring-4 focus:ring-gray-300 enabled:hover:bg-gray-800',
    info: 'border border-transparent bg-cyan-700 text-white focus:ring-4 focus:ring-cyan-300 enabled:hover:bg-cyan-800',
    light:
      'border border-transparent bg-titan-night-700 text-white focus:ring-4 focus:ring-titan-night-300 enabled:hover:bg-titan-night-800',
    lighter:
      'border border-transparent bg-gray-100 text-gray-800 focus:ring-4 focus:ring-gray-200 enabled:hover:bg-gray-200',
    purple:
      'border border-transparent bg-purple-700 text-white focus:ring-4 focus:ring-purple-300 enabled:hover:bg-purple-800',
    success:
      'border border-transparent bg-green-700 text-white focus:ring-4 focus:ring-green-300 enabled:hover:bg-green-800',
    warning:
      'border border-transparent bg-yellow-600 text-white focus:ring-4 focus:ring-yellow-300 enabled:hover:bg-yellow-700',
    blue: 'border border-transparent bg-titan-blue-500 text-dark focus:ring-4 focus:ring-titan-blue-300 enabled:hover:bg-titan-blue-800',
    cyan: 'border border-transparent bg-cyan-700 text-white focus:ring-4 focus:ring-cyan-300 enabled:hover:bg-cyan-800',
    green:
      'border border-transparent bg-green-700 text-white focus:ring-4 focus:ring-green-300 enabled:hover:bg-green-800',
    indigo:
      'border border-transparent bg-indigo-700 text-white focus:ring-4 focus:ring-indigo-300 enabled:hover:bg-indigo-800',
    lime: 'border border-transparent bg-lime-700 text-white focus:ring-4 focus:ring-lime-300 enabled:hover:bg-lime-800',
    pink: 'border border-transparent bg-pink-700 text-white focus:ring-4 focus:ring-pink-300 enabled:hover:bg-pink-800',
    red: 'border-transparent bg-titan-folly-500 text-white focus-visible:ring-4 focus-visible:ring-titan-folly-300 enabled:hover:bg-titan-folly-600',
    teal: 'border border-transparent bg-teal-700 text-white focus:ring-4 focus:ring-teal-300 enabled:hover:bg-teal-800',
    yellow:
      'border border-transparent bg-titan-yellow-500 text-titan-night-900 focus:ring-4 focus:ring-titan-yellow-300 enabled:hover:bg-titan-yellow-700',
  },
  disabled: 'cursor-not-allowed opacity-50',
  isProcessing: 'cursor-wait',
  spinnerSlot: 'absolute top-0 flex h-full items-center',
  spinnerLeftPosition: {
    xs: 'left-2',
    sm: 'left-3',
    md: 'left-4',
    lg: 'left-5',
    xl: 'left-6',
  },
  gradient: {
    cyan: 'bg-linear-to-r from-cyan-400 via-cyan-500 to-cyan-600 text-white focus:ring-4 focus:ring-cyan-300 enabled:hover:bg-linear-to-br',
    failure:
      'bg-linear-to-r from-red-400 via-red-500 to-red-600 text-white focus:ring-4 focus:ring-red-300 enabled:hover:bg-linear-to-br',
    info: 'bg-linear-to-r from-cyan-500 via-cyan-600 to-cyan-700 text-white focus:ring-4 focus:ring-cyan-300 enabled:hover:bg-linear-to-br',
    lime: 'text-titan-night-900 bg-linear-to-r from-lime-200 via-lime-400 to-lime-500 focus:ring-4 focus:ring-lime-300 enabled:hover:bg-linear-to-br',
    pink: 'bg-linear-to-r from-pink-400 via-pink-500 to-pink-600 text-white focus:ring-4 focus:ring-pink-300 enabled:hover:bg-linear-to-br',
    purple:
      'bg-linear-to-r from-purple-500 via-purple-600 to-purple-700 text-white focus:ring-4 focus:ring-purple-300 enabled:hover:bg-linear-to-br',
    success:
      'bg-linear-to-r from-green-400 via-green-500 to-green-600 text-white focus:ring-4 focus:ring-green-300 enabled:hover:bg-linear-to-br',
    teal: 'bg-linear-to-r from-teal-400 via-teal-500 to-teal-600 text-white focus:ring-4 focus:ring-teal-300 enabled:hover:bg-linear-to-br',
  },
  gradientDuoTone: {
    cyanToBlue:
      'bg-linear-to-r from-cyan-500 to-cyan-500 text-white focus:ring-4 focus:ring-cyan-300 enabled:hover:bg-linear-to-bl',
    greenToBlue:
      'bg-linear-to-br from-green-400 to-cyan-600 text-white focus:ring-4 focus:ring-green-200 enabled:hover:bg-linear-to-bl',
    pinkToOrange:
      'bg-linear-to-br from-pink-500 to-orange-400 text-white focus:ring-4 focus:ring-pink-200 enabled:hover:bg-linear-to-bl',
    purpleToBlue:
      'bg-linear-to-br from-purple-600 to-cyan-500 text-white focus:ring-4 focus:ring-cyan-300 enabled:hover:bg-linear-to-bl',
    purpleToPink:
      'bg-linear-to-r from-purple-500 to-pink-500 text-white focus:ring-4 focus:ring-purple-200 enabled:hover:bg-linear-to-l',
    redToYellow:
      'text-titan-night-900 bg-linear-to-r from-red-200 via-red-300 to-yellow-200 focus:ring-4 focus:ring-red-100 enabled:hover:bg-linear-to-bl',
    tealToLime:
      'text-titan-night-900 enabled:hover:text-titan-night-900 bg-linear-to-r from-teal-200 to-lime-200 focus:ring-4 focus:ring-lime-200 enabled:hover:bg-linear-to-l enabled:hover:from-teal-200 enabled:hover:to-lime-200',
  },
  inner: {
    base: 'flex items-stretch transition-all duration-(--duration-base)',
    position: {
      none: '',
      start: 'rounded-r-none',
      middle: 'rounded-none',
      end: 'rounded-l-none',
    },
    outline: 'border border-transparent',
    isProcessingPadding: {
      xs: 'pl-8',
      sm: 'pl-10',
      md: 'pl-12',
      lg: 'pl-16',
      xl: 'pl-20',
    },
  },
  label:
    'ml-2 inline-flex h-4 w-4 items-center justify-center rounded-none bg-cyan-200 text-xs font-semibold text-cyan-800',
  outline: {
    color: {
      // Gray outline: override outline.on's `group-hover:text-white` so the
      // label stays readable on hover. Background stays white (no tint).
      gray: 'border border-gray-900 text-gray-900 hover:[&>span]:!text-gray-900',
      // Red outline: MUI-style hover (subtle white-pink fill + darker red text).
      // `[&>span]:hover:!...` targets the inner span from the outer button so
      // we override outline.on's shared `group-hover:text-white` (which makes
      // every other outline color fill-on-hover). Pattern mirrored from
      // features/submit-list/modals/CleanedDataModal.tsx:222. Scoped to red
      // only — other outline colors keep their existing behavior.
      red: 'text-titan-folly-500 hover:[&>span]:!bg-[#fffcfd] hover:[&>span]:!text-titan-folly-600',
      default: 'border-0',
      light: 'border border-gray-300 text-gray-700 hover:text-white',
    },
    off: '',
    on: 'flex w-full justify-center bg-white transition-[background-color] duration-(--duration-fast) ease-in-quart group-enabled:group-hover:bg-opacity-0 group-enabled:group-hover:text-white',
    pill: {
      on: 'rounded-lg',
    },
  },
  pill: {
    off: 'rounded-lg',
    on: 'rounded-none',
  },
  size: {
    xs: 'px-2 py-1 text-xs',
    sm: 'px-3 py-1.5 text-sm',
    md: 'px-4 py-2 text-sm',
    lg: 'px-5 py-2.5 text-base',
    xl: 'px-6 py-3 text-base',
  },
};
